"use client";

import React, { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";

/**
 * Single source of truth for the site's mobile accordion.
 *
 * Two families of components collapse into an accordion below `lg` and must
 * look identical while doing it:
 *
 *   • "Core Capabilities" tabs — {@link ../common/TabbedContent} on SHRED,
 *     Scarf and Core Services.
 *   • Numbered step lists — the vertical `Tabs` on OT Security, Secure Code
 *     Review and Compromise Assessment, plus the hand-rolled APT phase list on
 *     Adversary Simulation.
 *
 * Shared rules: one card with hairline separators, a 56px header row carrying a
 * chevron that flips when open, and a panel whose height animates through
 * `grid-template-rows: 0fr → 1fr` (no JS measuring, works with SSR). The group
 * starts fully collapsed, opens one row at a time, and tapping the open row
 * closes it again.
 *
 * Each constant is the mobile half of the styling only; components append their
 * own `lg:` classes for the desktop layout they revert to.
 */

/**
 * One easing curve for everything that moves here (panel height, chevron, copy)
 * so an opening row reads as a single motion instead of three.
 */
const EASE = "ease-[cubic-bezier(0.4,0,0.2,1)]";

/** Card wrapping the rows. */
export const accordionCard = "w-full bg-[#171A1D] overflow-hidden rounded-[8px] sm:rounded-[10px]";

/** Hairline between rows — applied from the second row on. */
export const accordionDivider = "border-t border-white/[0.07]";

/** Header row: label (plus a number, where the design has one) + chevron. */
export const accordionHeader = `w-full flex items-center gap-4 text-left px-4 py-[14px] sm:px-5 min-h-[56px] sm:min-h-[60px] transition-colors duration-200 ${EASE} cursor-pointer font-[var(--font-avenir)] font-semibold text-[15px] sm:text-[16px] leading-snug tracking-[0px]`;

/** Animated panel wrapper; pair with {@link accordionPanelClip} inside. */
export const accordionPanel = `grid self-start overflow-hidden transition-[grid-template-rows] duration-[380ms] ${EASE} motion-reduce:transition-none`;
export const accordionPanelOpen = "grid-rows-[1fr]";
export const accordionPanelClosed = "grid-rows-[0fr]";

/** Required inner clipper: keeps the collapsing row from scrolling its copy. */
export const accordionPanelClip = "min-h-0 overflow-hidden";

/**
 * Copy inside the panel. The copy fades and lifts into place on its own, a beat
 * behind the row opening ({@link accordionBodyOpen} carries the delay) and
 * immediately on the way out, so the text never sits fully lit inside a row that
 * is still half its height.
 */
export const accordionBody = `px-4 pt-2 pb-6 sm:px-5 sm:pb-8 font-[var(--font-avenir)] font-normal text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] tracking-[0.4px] sm:tracking-[0.45px] text-white transition-[opacity,transform] duration-300 ${EASE} motion-reduce:transition-none`;
export const accordionBodyOpen = "opacity-100 translate-y-0 delay-[90ms]";
export const accordionBodyClosed = "opacity-0 -translate-y-[6px] delay-0";

/** Panel background — Figma "Core Capabilities" gradient. */
export const ACCORDION_PANEL_BG = "linear-gradient(360deg, #15181D 1.535%, #1F2428 100%)";

/**
 * Same 380ms as the `duration-[380ms]` in {@link accordionPanel}. Tailwind only
 * sees class names it can read as literal text, so the two cannot share one
 * value — change this and change the class.
 */
const PANEL_MS = 380;

/** Keep chasing a few frames past the transition, in case it lands late. */
const ANCHOR_MS = PANEL_MS + 80;

/** `lg` — where every accordion here reverts to its desktop layout. */
const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Keeps the header you just tapped pinned where it is on screen.
 *
 * Opening a row collapses the row that was open, and when that row sits *above*
 * the one you tapped, everything below it — the tapped header included — slides
 * up by the height the closing panel gives back. On a tall panel that is several
 * hundred pixels, enough to throw the row you asked for off the top of the
 * screen (Chrome's scroll anchoring does not catch it here and Safari has none).
 *
 * The panel gives that height back gradually, over the 380ms
 * `grid-template-rows` transition, so one corrective `scrollBy` cannot cover it:
 * it would overshoot on the first frame and drift for the rest. Instead this
 * re-measures the header every frame until the transition is done and scrolls by
 * whatever it moved, which needs no knowledge of the panel's height and
 * self-corrects if anything else on the page resizes mid-animation. Under
 * `prefers-reduced-motion` the panel snaps shut and the first frame covers it.
 *
 * Call it from the header's `onClick` with that header element, *before* the
 * state update, so the position it captures is the one the user is looking at:
 *
 *     const anchorRow = useAccordionScrollAnchor();
 *     <button onClick={(e) => { anchorRow(e.currentTarget); toggle(id); }} />
 *
 * No-ops where there is nothing to correct: on desktop, when closing the open
 * row (the height leaves from *below* the header), and when opening a row above
 * the open one. At the very top of the document the browser clamps `scrollY` at
 * 0 so the last pixels cannot be given back — harmless, since nothing is out of
 * view up there.
 */
export function useAccordionScrollAnchor() {
  const frameRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const stop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  return useCallback(
    (el: HTMLElement | null) => {
      // A second tap supersedes the first.
      stop();
      if (!el || typeof window === "undefined") return;
      // Desktop keeps every header in row 1 of the grid, so none of them move.
      if (window.matchMedia(DESKTOP_QUERY).matches) return;

      const top = el.getBoundingClientRect().top;
      const deadline = performance.now() + ANCHOR_MS;

      // Chasing the header would fight a user who starts scrolling mid-animation.
      // `scroll` is deliberately not in this list: `scrollBy` below fires it, so
      // listening for it would abort on our own correction.
      const abort = () => stop();
      window.addEventListener("wheel", abort, { passive: true, once: true });
      window.addEventListener("touchstart", abort, { passive: true, once: true });
      window.addEventListener("keydown", abort, { once: true });
      cleanupRef.current = () => {
        window.removeEventListener("wheel", abort);
        window.removeEventListener("touchstart", abort);
        window.removeEventListener("keydown", abort);
      };

      const tick = () => {
        // Animations are advanced before rAF callbacks and both run before
        // paint, so this reads the height the frame is about to draw and the
        // correction lands without a visible step.
        const drift = el.getBoundingClientRect().top - top;
        if (Math.abs(drift) >= 0.5) window.scrollBy(0, drift);
        if (performance.now() < deadline) {
          frameRef.current = requestAnimationFrame(tick);
        } else {
          stop();
        }
      };
      frameRef.current = requestAnimationFrame(tick);
    },
    [stop]
  );
}

/** Chevron in the header row; flips to point up while the row is open. */
export const AccordionChevron = ({ open, className }: { open: boolean; className?: string }) => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={clsx(
      "shrink-0 w-[18px] h-[18px] transition-transform duration-[380ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
      open && "rotate-180",
      className
    )}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);
